/**
 * Committed workflow catalog overlay (ADR-1 / ADR-3).
 *
 * The condition operators, boolean logic, and the set of trigger event types are
 * generated by the control-plane OpenAPI (`Operator`, `BooleanLogic`, `EventType` in
 * `./index.ts`); this module re-exports those as the editor's vocabulary so the
 * picker can never drift from the backend contract. The per-event ATTRIBUTE schemas
 * are generated as well: `./eventCatalog.ts` renders them from the committed
 * event catalog artifact (ENG-11425), which the backend derives from the event
 * envelopes themselves, so an attribute's key, field type and enum vocabulary are
 * the backend's own and cannot be transcribed wrongly.
 *
 * Committed generated source rather than a schema fetched at run time, because the
 * literals are what make the editor checkable: `type` is the `CatalogFieldType`
 * literal union the operator table is keyed by, `EVENT_CATALOG` is `as const
 * satisfies`-tied so each `key` survives as a literal, `AttributeKeyFor<E>` exposes
 * those keys as a union, and the `Record<EventType, …>` maps built from this catalog
 * (label keys, result schemas) make a newly offered event or action a compile error
 * until it is registered — `EVENT_ATTRIBUTE_LABEL_KEY` names the very attribute whose
 * display label is missing.
 *
 * Hand-authored here is the editorial layer the artifact does not carry, and only
 * that: which event types the picker offers, the operator-by-field-type table, each
 * event's scope and timing surface, and the per-attribute judgements below — the
 * display order authors read, whether a field is reported only intermittently
 * ({@link CatalogAttribute.sparse}), whether the token menu offers it, and its
 * display unit. Offering a new event type is therefore a deliberate edit here even
 * once its schema is generated.
 *
 * Not every `EventType` member is offered: `condition.opened` / `condition.resolved`
 * are synthesized internally by temporal-modifier decomposition and are deliberately
 * excluded from `EVENT_CATALOG`, and `scheduled` is authored through the schedule
 * editor rather than against attributes. The "coming soon" mechanism
 * (`SUPPORTED_EVENT_TYPES`) surfaces a catalog event as disabled until the builder
 * can author it.
 *
 * The seam a type cannot close is the artifact's freshness: a vendored copy older
 * than the backend describes the events as they were when it was fetched, so
 * `pnpm run event-catalog:fetch && pnpm run event-catalog:generate` is what keeps
 * this module current. `ConditionBase.field` is likewise a runtime `string` — a
 * stored condition naming an attribute the catalog does not declare is caught by
 * `findUnknownReferences` and the backend validator, not by `tsc`.
 *
 * Field-path convention: the catalog stores BARE attribute keys (`status`,
 * `latest_soc`). The backend `Condition.field` and the templating layer use the
 * full dotted path `event.attributes.<key>` (`resolve_field` splits on `.`). The
 * serializer bridge (`modelDefinition`) is the single seam that adds this prefix
 * on the way out and strips it on the way in — see {@link EVENT_ATTRIBUTE_PREFIX}.
 *
 * Attribute keys and enum identifiers below are catalog DATA and are never
 * localized — they are what the templating layer resolves and the validator
 * checks against. Human-facing display text is localized via `public/locales`:
 * the trigger picker's cards (`TRIGGER_PICKER_ENTRIES`) name their own title and
 * description keys, which is what lets two cards on one event read differently;
 * event-type labels resolve through the `workflows.eventCatalog` namespace (via
 * `useFormatters().workflows.eventType`), attribute labels through
 * `workflows.eventAttributes` (via `useFormatters().workflows.eventAttribute`,
 * see `src/data/formatters/workflows.ts`), and UI chrome — operator names,
 * section titles, buttons — through `workflows.builder`.
 */

import type { MessageKeys, Messages, NestedKeyOf, NestedValueOf } from 'next-intl'

import type {
  ActionStepDefinition,
  BooleanLogic,
  ChargerRemoteStartResult,
  ChargerResetResult,
  EventType,
  FetchDataParametersAuthoring,
  FetchDataResult,
  FetchedCharger,
  FetchedSite,
  FetchedVehicle,
  FillPolicy,
  NotificationCreateResult,
  Operator,
  ReportFormat,
  RunReportResult,
  SendEmailResult,
  SendSmsResult,
  WaitResult
} from '~/data/api/client/workflows'

import { EVENT_ATTRIBUTES, EVENT_CONTEXT } from './eventCatalog'

// ---- Field types & operators -------------------------------------------

/**
 * Attribute value kinds the editor knows how to author against. Mirrors the backend
 * field-type vocabulary minus `object`: a nested payload carries no operator and
 * cannot be interpolated into a template, so the generator leaves such attributes
 * out of the schemas it emits and the editor never has one to render.
 */
export type CatalogFieldType = 'string' | 'number' | 'boolean' | 'enum' | 'datetime'

/** Condition operators — the generated backend `Operator` enum. */
export type CatalogOperator = Operator

/** Boolean combinator for grouped conditions — the generated backend enum. */
export type { BooleanLogic }

/**
 * Operator-by-field-type table mirroring the backend `OPERATORS_BY_FIELD_TYPE`
 * matrix. The editor only offers operators listed here for a given field type,
 * and the validator rejects any combination outside it (mirrors the backend
 * `operator_not_valid_for_type`).
 */
export const OPERATORS_BY_FIELD_TYPE: Record<CatalogFieldType, readonly CatalogOperator[]> = {
  string: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'STARTS_WITH', 'ENDS_WITH', 'ONE_OF'],
  number: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL', 'BETWEEN', 'ONE_OF'],
  // The backend also permits EQUALS on boolean, but the editor intentionally
  // offers only IS_TRUE/IS_FALSE for clarity.
  boolean: ['IS_TRUE', 'IS_FALSE'],
  enum: ['EQUALS', 'NOT_EQUALS', 'ONE_OF'],
  datetime: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL', 'BETWEEN']
}

// ---- Event catalog ------------------------------------------------------

/** Domain grouping used to section the trigger picker. */
export type TriggerPickerCategory = 'charger' | 'vehicle' | 'synoplink' | 'der' | 'site' | 'schedule'

/**
 * Section order for the trigger picker. `schedule` sits last: a scheduled
 * trigger fires on a timer rather than off an asset feed, so it reads as the
 * catch-all rather than a domain alongside the others.
 */
export const CATEGORY_ORDER: readonly TriggerPickerCategory[] = [
  'charger',
  'site',
  'vehicle',
  'synoplink',
  'der',
  'schedule'
]

/**
 * The asset domain a trigger's scope ("Applies to") narrows over (ENG-9403).
 * Drives which entity selectors the scope inspector renders: `charger`/`vehicle`/
 * `site` expose their asset pickers, while `account` (system or scheduled events
 * with no asset subject) offers only the org/site filter. `der` narrows by site
 * alone: the fault feed's subject is a DER asset, but the builder offers no
 * per-asset picker, so a DER workflow names the population by where its assets
 * sit, and the kind it watches comes from the card it was authored from.
 */
export type CatalogScopeDomain = 'charger' | 'synoplink' | 'vehicle' | 'der' | 'site' | 'account'

/**
 * The kind of value a scope field narrows by; maps to a picker + a selected-value
 * list of ids.
 */
export type ScopeEntityKind = 'organization' | 'site' | 'charger' | 'synopLink' | 'vehicle'

/**
 * A scopeable attribute on an event: the canonical condition `field` and the
 * value kind it filters by. Scope selections serialize into the trigger's own
 * `scope` predicate as `ONE_OF` leaves on these fields; the field MUST be a real
 * attribute on the event so the backend validator accepts the resulting filter.
 */
export type CatalogScopeField = {
  field: string
  entity: ScopeEntityKind
}

/**
 * DER asset kinds the fault feed's `asset_type` enum can carry. Decode/label
 * concerns (a persisted scope, the condition editor's generic enum handling)
 * work against this full set, since a workflow saved before the offered list
 * narrowed — or one holding a kind the backend added since — still names one
 * of these eight.
 *
 * Spelled out rather than read off the generated schema so the label map and the
 * offered subset below can key off literals, and checked against it member for
 * member: a kind the feed gains or reorders is a compile error here.
 */
export const DER_ASSET_TYPES = [
  'BESS',
  'GENSET',
  'PANEL',
  'INVERTER',
  'SOLAR_ARRAY',
  'FUEL_TANK',
  'LOAD',
  'GRID_CONNECTION'
] as const satisfies GeneratedEnumValues<'der.fault', 'asset_type'>

/**
 * DER asset kinds the builder authors a fault trigger for — one picker card each
 * (see {@link TRIGGER_PICKER_ENTRIES}). Narrower than {@link DER_ASSET_TYPES}:
 * only battery storage and generators have a device integration that publishes
 * fault alarm transitions today. Grows as more integrations add fault-alarm
 * emission, a card per kind.
 */
export const DER_FAULT_ASSET_TYPES = ['BESS', 'GENSET'] as const satisfies readonly (typeof DER_ASSET_TYPES)[number][]

/** A DER asset kind the builder can author a fault trigger for. */
export type DerFaultAssetType = (typeof DER_FAULT_ASSET_TYPES)[number]

/** The fault feed itself — the event both DER fault cards are authored against. */
export const DER_FAULT_EVENT_TYPE = 'der.fault' as const satisfies EventType

/**
 * Entity kinds the connectivity feed's `entity_type` can name. The feed's value
 * space is the platform's and grows server-side, so this is what the catalog can
 * describe rather than everything the wire may carry; the builder authors the
 * subset it has a scope picker for (`NOT_HEARD_FROM_ENTITY_TYPES`).
 */
export const CONNECTIVITY_ENTITY_KINDS = ['charger', 'synoplink'] as const

/** The connectivity feed itself — the event a not-heard-from trigger is authored against. */
export const CONNECTIVITY_EVENT_TYPE = 'entity.connectivity' as const satisfies EventType

/** An entity kind the connectivity feed reports on. */
export type ConnectivityEntityKind = (typeof CONNECTIVITY_ENTITY_KINDS)[number]

/**
 * Scope shape per watched entity kind for the connectivity feed. `organization_id`
 * and `site_id` mean the same thing whichever kind is watched, but `entity_id` names
 * a different asset — a charger id for a charger, a SynopLink's thing name for a
 * SynopLink — so the picker behind that dimension differs by kind. A SynopLink is a
 * device on a site rather than a charger, hence its own scope domain.
 */
export const CONNECTIVITY_SCOPE_VARIANTS = {
  charger: {
    scopeDomain: 'charger',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'entity_id', entity: 'charger' }
    ]
  },
  synoplink: {
    scopeDomain: 'synoplink',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'entity_id', entity: 'synopLink' }
    ]
  }
} as const satisfies Record<
  ConnectivityEntityKind,
  { scopeDomain: CatalogScopeDomain; scopeFields: readonly CatalogScopeField[] }
>

/** Whether a persisted `entity_type` is one the catalog describes a scope shape for. */
export function isConnectivityEntityKind(entityType: string): entityType is ConnectivityEntityKind {
  return (CONNECTIVITY_ENTITY_KINDS as readonly string[]).includes(entityType)
}

/**
 * The edges of a DER fault the feed publishes — the `transition` enum. Both are
 * real events on the same asset, so a trigger left unfiltered fires when the
 * fault clears as well as when it appears. Checked against the generated schema,
 * whose member order is the backend's own.
 */
export const DER_FAULT_TRANSITIONS = ['RAISED', 'CLEARED'] as const satisfies GeneratedEnumValues<
  'der.fault',
  'transition'
>

/**
 * How urgently a DER fault reads — the `severity` enum, from least to most, which
 * is the order the feed declares. Named (rather than left inside the generated
 * schema) so the condition editor can title-case these two vocabularies' options
 * for display without reaching into every event's enum values — see
 * `enumOptionLabel`.
 */
export const DER_FAULT_SEVERITIES = ['WARNING', 'ERROR', 'CRITICAL'] as const satisfies GeneratedEnumValues<
  'der.fault',
  'severity'
>

export type CatalogAttribute = {
  /** Canonical attribute key used for matching/serialization; never localized. */
  key: string
  type: CatalogFieldType
  /** Allowed values for `enum` fields. */
  enumValues?: readonly string[]
  /** Display-only unit hint for `number` fields. */
  unit?: string
  /**
   * Whether the template token menu offers this attribute (defaults to `true`).
   * `false` is an editorial judgement about the picker's vocabulary, not a claim
   * about the data: the attribute is populated and interpolates fine, so a
   * hand-typed or API-authored `{{event.attributes.<key>}}` stays valid
   * (`findUnknownReferences`) and still renders as a labelled chip
   * (`useTokenResolver`). Use it for a field whose value the trigger fixes for
   * the whole workflow, or that names an internal identity the author already
   * has a friendlier handle for — offering it invites a token that reads as a
   * constant or as plumbing.
   */
  offeredAsToken?: boolean
  /**
   * Whether the condition editor offers this attribute as a field to filter on
   * (defaults to `true`). The condition-side twin of {@link offeredAsToken}, and
   * the same editorial judgement: the attribute is populated and a persisted
   * condition on it still hydrates, renders and validates. Use it for a field
   * whose value the trigger fixes for the whole workflow, where a filter on it
   * could only restate the trigger or exclude everything it watches.
   */
  offeredAsCondition?: boolean
  /**
   * Whether the value renders title-cased for display (`RAISED` → `Raised`),
   * while the stored and wire value stay the untouched original. An editorial
   * judgement about an all-caps vocabulary that reads as prose — stated on the
   * attribute itself, so an unrelated enum that happens to declare the same
   * members renders exactly as authored.
   */
  titleCased?: boolean
  /**
   * Whether the pipeline carries this attribute only intermittently: it is set
   * when the upstream provider or producer reports it and left null the rest of
   * the time (state of charge on vehicle telemetry, provider-decoded fault
   * fields, stop-only session-summary fields). The attribute is fully authorable;
   * the flag is a caveat about match frequency, not a claim that the field is
   * unusable.
   *
   * A condition on a sparse attribute matches on a fraction of the event stream,
   * which changes what a firing mode does: a sustained window over a gauge that
   * reports once in ten messages leans entirely on its gap policy, and an
   * aggregate count over one counts reports rather than reality. The Timing panel
   * surfaces a hint when the authored condition touches such a field (see
   * `FiringModeEditor`), so the caveat reaches the author at the control it
   * affects.
   */
  sparse?: boolean
}

// ---- Generated schemas & their editorial overlay ------------------------

/** The attribute schema `./eventCatalog` declares for one event type. */
type GeneratedAttributes<E extends EventType> = (typeof EVENT_ATTRIBUTES)[E]

/**
 * The member list the generated schema declares for one enum attribute, as a tuple
 * of literals. A constant declared `as const satisfies GeneratedEnumValues<…>` is
 * therefore checked member for member and in order against the backend's vocabulary.
 */
type GeneratedEnumValues<E extends EventType, K extends GeneratedAttributes<E>[number]['key']> = Extract<
  GeneratedAttributes<E>[number],
  { key: K; enumValues: readonly string[] }
>['enumValues']

/**
 * What the builder makes of an attribute the artifact only names and types: whether
 * ingestion populates it, how reliably, whether the token menu offers it, and the
 * unit its number reads in. Every one of these is a claim about this app or about the
 * ingestion pipeline rather than about the event contract, which is why they are
 * authored per event in `EVENT_CATALOG` instead of generated.
 */
type AttributeMetadata = Pick<CatalogAttribute, 'offeredAsCondition' | 'offeredAsToken' | 'sparse' | 'titleCased' | 'unit'>

/**
 * A generated attribute schema with its editorial metadata folded in, keyed by
 * attribute key. The keys are constrained to the schema's own, so metadata left
 * behind by a renamed or dropped attribute is a compile error naming it rather than
 * an entry that silently applies to nothing.
 *
 * The return type maps over the schema, so every literal `key` survives for
 * `AttributeKeyFor` and the folded-in fields read back off the result.
 */
/**
 * A generated attribute schema in the builder's own display order. The artifact
 * sorts attributes by name, but what order an author reads them in is editorial —
 * three surfaces consume it: the condition field dropdown, the token menu, and
 * which field a fresh condition seeds on (the first offered attribute). Each
 * entry states its order beside its other editorial choices.
 *
 * The order must name every generated key exactly once. A key the backend adds
 * is offered nowhere until it is placed here deliberately, and this throws
 * naming it — at module load, so any test or render fails fast — rather than
 * dropping it silently.
 *
 * The entity-context fields are ordered by the same rule; see
 * {@link CONTEXT_FIELD_ORDER}.
 */
function orderedAttributes<A extends readonly { key: string }[]>(
  attributes: A,
  order: readonly A[number]['key'][]
): readonly A[number][] {
  const byKey = new Map(attributes.map((attribute) => [attribute.key, attribute] as const))
  const missing = [...byKey.keys()].filter((key) => !order.includes(key))
  const unknown = order.filter((key) => !byKey.has(key))
  if (missing.length > 0 || unknown.length > 0 || order.length !== attributes.length) {
    throw new Error(
      `orderedAttributes: the order must be a permutation of the generated keys` +
        (missing.length > 0 ? `; unplaced: ${missing.join(', ')}` : '') +
        (unknown.length > 0 ? `; unknown: ${unknown.join(', ')}` : '')
    )
  }
  return order.map((key) => byKey.get(key) as A[number])
}

function withAttributeMetadata<A extends readonly CatalogAttribute[]>(
  attributes: A,
  metadata: Partial<Record<A[number]['key'], AttributeMetadata>>
): { [I in keyof A]: A[I] & AttributeMetadata } {
  const byKey = metadata as Record<string, AttributeMetadata | undefined>
  return attributes.map((attribute) => ({ ...attribute, ...byKey[attribute.key] })) as {
    [I in keyof A]: A[I] & AttributeMetadata
  }
}


// ---- Firing modes -------------------------------------------------------

/**
 * The firing modes a trigger's Timing panel can offer, in escalating cost order.
 * `every` is the absence of a temporal modifier — one run per matching event.
 */
export type CatalogFiringMode = 'immediate' | 'sustained' | 'aggregate' | 'every'

/** A named cause the Timing panel can state for a withheld mode. */
export type CatalogUnavailableReason = 'multiAlarm'

/** Canonical display order for firing modes; the Timing panel renders in it. */
export const FIRING_MODE_ORDER: readonly CatalogFiringMode[] = ['immediate', 'sustained', 'aggregate', 'every']

/**
 * Whether a firing mode fires on a condition *changing state*, and so needs a
 * condition to watch.
 *
 * `immediate` fires when the condition becomes true and re-arms when it goes
 * false again; `sustained` fires once it has held true for a window. On a
 * trigger carrying no condition both are degenerate — every event matches, the
 * reading is true from the first one onward, and no contrary reading ever
 * arrives to resolve it. `aggregate` counts events and `every` fires on each,
 * so neither needs one.
 *
 * Three surfaces turn on this single fact: the validator raises the blocking
 * issue, the Timing panel says it at the control that creates the situation, and
 * the catalog seeds a condition for every event it defaults to one of these
 * modes (see `builderModel.defaultConditionForEvent`).
 */
export function firingModeNeedsCondition(mode: CatalogFiringMode): boolean {
  return mode === 'immediate' || mode === 'sustained'
}

/**
 * An event's timing surface: which firing modes the Timing panel offers for it,
 * which one a freshly picked trigger starts on, and how a sustained window seeds
 * its gap policy (ENG-10941).
 *
 * ---- Classifying an event ----
 *
 * A mode is offered only where the shape of the signal makes that mode's own
 * sentence true. The panel's copy promises a behavior; this table is what keeps
 * an author from configuring a promise the engine cannot keep.
 *
 * `immediate` and `sustained` (with `hold_last`) resolve on a contrary reading,
 * so they require the correlation key's state to be SINGLE-VALUED: every in-scope
 * event must be a statement about that key's whole state. A gauge reading is one
 * — the vehicle IS at 14% — and so is a connector status, which holds exactly one
 * value at a time. A multi-instance edge stream is not: a DER fault feed carries
 * RAISED/CLEARED edges per alarm while one asset holds several alarms at once, so
 * the per-key last-event-wins level projection lets one alarm's CLEARED resolve an
 * episode another alarm is still holding open, and swallows overlapping alarms
 * entirely. Having a contrary reading is necessary but not sufficient —
 * single-valuedness is the criterion. Per-alarm episode keying (a composite
 * correlation key, or an alarm instance as the subject) is what would make
 * `immediate` honest on such a feed.
 *
 * A filtered condition sharpens that requirement. Under the contrary-reading
 * modes, EVERY in-scope non-matching event resolves the episode, so a condition
 * narrower than the key's full state — one severity, one code — is falsely
 * resolved by unrelated in-scope traffic.
 *
 * `sustained` also requires the feed to keep emitting while the condition holds.
 * A signal that goes silent once true — a raised fault that reports nothing
 * further — starves the window rather than filling it.
 *
 * `aggregate` counts events, so it means something only where an event IS an
 * occurrence: a state change, a fault, a boot — flap and storm detection. On a
 * periodic snapshot feed a count is a duration measured in sweep ticks, silently
 * coupled to the sweep cadence; `sustained` is the honest spelling of that intent.
 *
 * `defaultMode` biases against spam and against noise at once: per-episode
 * (`immediate`) for gauges and statuses; `sustained` where the gauge is noisy
 * enough that per-crossing episodes would flap (a site's power draw); `aggregate`
 * for occurrence streams that repeat-report the same trouble (vehicle faults);
 * `every` only where a single event is meaningful on its own and arrives at human
 * scale (transactions, boots). `every` is offered wherever modes apply at all, but
 * it is never the silent default on a chatty stream.
 *
 * A default acts at the seam a trigger acquires its event through
 * (`nextTriggerForEvent`) and nowhere else. Hydration is truth-first: a stored
 * definition renders the mode it was authored with, including one this table does
 * not offer.
 *
 * The `dedicated` and `none` variants mark an event whose timing is authored
 * somewhere other than the Timing panel — the not-heard-from window is a control
 * of its own, and a schedule is its own cadence — where a mode list would be a
 * fiction rather than a narrower set.
 */
export type CatalogTiming =
  | {
      kind: 'modes'
      /** Modes offered for this event, a subset of {@link FIRING_MODE_ORDER}. */
      modes: readonly CatalogFiringMode[]
      /** Mode a newly picked trigger on this event starts on; always a member of `modes`. */
      defaultMode: CatalogFiringMode
      /**
       * Gap policy a freshly selected `sustained` window starts on: `hold_last`
       * for a gauge or level, where the last reading stands until the next one.
       * `null` where the event does not offer `sustained`.
       */
      sustainedFill: FillPolicy | null
      /**
       * Names the reason a withheld mode is withheld where this event's reason
       * differs from the one its family shares. The Timing panel maps each name to
       * its copy (`FiringModeEditor`); the catalog stays free of message keys.
       * Omitted wherever the shared sentence is already true, which is most events.
       */
      unavailableReason?: Partial<Record<CatalogFiringMode, CatalogUnavailableReason>>
    }
  /** Timing is authored by a control of the event's own — the not-heard-from window. */
  | { kind: 'dedicated' }
  /** The event has no timing surface at all: a schedule is its own cadence. */
  | { kind: 'none' }

export type CatalogEventType = {
  /** The generated `EventType` this catalog entry describes; never localized. */
  eventType: EventType
  attributes: readonly CatalogAttribute[]
  /** The asset domain this trigger's scope narrows over (ENG-9403). */
  scopeDomain: CatalogScopeDomain
  /**
   * Attributes the scope panel can filter by, ordered broad → narrow
   * (org → site → asset). Empty until the event's attribute schema is verified.
   */
  scopeFields: readonly CatalogScopeField[]
  /**
   * The firing modes the Timing panel offers for this event and the one a newly
   * picked trigger starts on (ENG-10941). Required: a new event without a stated
   * timing surface would silently inherit whatever the panel renders by default,
   * so leaving it out is a compile error naming the entry.
   */
  timing: CatalogTiming
}

/**
 * Trigger event types the editor can fully author against today (a verified
 * attribute schema exists below, or — for `scheduled` — a dedicated editor). A
 * catalog entry whose `EventType` is absent from this set renders disabled
 * ("coming soon") until its schema lands; today every `EVENT_CATALOG` entry is
 * supported. (`condition.*` events are excluded from the catalog entirely, not
 * merely disabled — see the module header.)
 */
export const SUPPORTED_EVENT_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'charger.status_notification',
  'charger.start_transaction',
  'charger.stop_transaction',
  'charger.boot_notification',
  'charger.data_transfer',
  'charger.transaction_summary',
  'vehicle.telemetry',
  'vehicle.fault',
  'der.fault',
  'site.power_utilization',
  // The connectivity feed behind the not-heard-from trigger (ENG-10875). It is
  // authored through the window editor and the scope panel rather than a
  // condition editor, but its attributes carry the scope fields that panel writes.
  'entity.connectivity',
  // A scheduled trigger has no attribute schema; it is authored against the
  // schedule editor (ENG-9268) rather than a condition, so it is fully
  // supported despite carrying no attributes.
  'scheduled'
])

export function isEventTypeSupported(id: EventType): boolean {
  return SUPPORTED_EVENT_TYPES.has(id)
}

/**
 * The events a trigger can be authored against — each entry points at its generated
 * attribute schema, overlaid with the editorial metadata the artifact does not
 * carry, plus the scope shape the editor works from. Membership is the deliberate
 * part: `condition.opened` / `condition.resolved` are synthesized internally and
 * intentionally excluded. What the picker actually offers is
 * {@link TRIGGER_PICKER_ENTRIES}, which puts one or more cards in front of an
 * entry here.
 */
export const EVENT_CATALOG = [
  {
    eventType: 'charger.status_notification',
    scopeDomain: 'charger',
    // Scopes broad → narrow: org → site → charger. chargers-api publishes
    // `siteId` on the connector-status contract and the ingestion adapter maps it
    // through, so site scoping matches real events.
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'charger_id', entity: 'charger' }
    ],
    // A connector's status is a level: it holds until the charger reports a new
    // one, so there is always a current reading for `immediate` to detect a
    // crossing into and for `sustained` to hold across. Crossing into Faulted is
    // the thing worth acting on, hence `immediate` as the default; `sustained`
    // rules out a flap, and `aggregate` counts a connector cycling.
    timing: { kind: 'modes', modes: ['immediate', 'sustained', 'aggregate', 'every'], defaultMode: 'immediate', sustainedFill: 'hold_last' },
    // `severity` is derived by the connector_status ingestion adapter rather than
    // reported upstream: Faulted/PartiallyFaulted → 'fault'; any real error_code →
    // 'warning'.
    // `vendor_error_code` is marked sparse because only some manufacturers' firmware
    // sends one: `error_code` rides on every status notification, the vendor code on
    // roughly a third of them.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['charger.status_notification'], ['charger_id', 'charger_name', 'connector_id', 'site_id', 'organization_id', 'status', 'error_code', 'vendor_error_code', 'error_info', 'severity', 'timestamp']), {
      vendor_error_code: { sparse: true }
    })
  },
  // `charger.start_transaction` / `charger.stop_transaction` both scope org → site →
  // charger: the start/stop contracts carry `siteId` and the adapters map it through.
  // These are thin session-edge events; the session metrics (energy, power, SoC)
  // belong to `charger.transaction_summary`.
  {
    eventType: 'charger.start_transaction',
    scopeDomain: 'charger',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'charger_id', entity: 'charger' }
    ],
    // A session start is discrete: it happens once and nothing un-happens it, so
    // there is no contrary reading for `immediate` or `sustained` to resolve on.
    // One run per session start is the point of the trigger, so `every` is the
    // default; `aggregate` counts starts in a window.
    timing: { kind: 'modes', modes: ['aggregate', 'every'], defaultMode: 'every', sustainedFill: null },
    attributes: orderedAttributes(EVENT_ATTRIBUTES['charger.start_transaction'], ['id', 'charger_id', 'charger_name', 'connector_id', 'site_id', 'organization_id', 'ocpp_tag', 'start_timestamp'])
  },
  {
    eventType: 'charger.stop_transaction',
    scopeDomain: 'charger',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'charger_id', entity: 'charger' }
    ],
    // Discrete, like the session start: one run per session end is what an author
    // wants, and `aggregate` covers a burst of them.
    timing: { kind: 'modes', modes: ['aggregate', 'every'], defaultMode: 'every', sustainedFill: null },
    attributes: orderedAttributes(EVENT_ATTRIBUTES['charger.stop_transaction'], ['id', 'charger_id', 'charger_name', 'connector_id', 'site_id', 'organization_id', 'ocpp_tag', 'stop_timestamp', 'stop_reason', 'stop_event_actor'])
  },
  {
    eventType: 'charger.boot_notification',
    scopeDomain: 'charger',
    // Scopes broad → narrow: org → site → charger, matching the other charger
    // events — the boot-notification adapter populates `site_id`.
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'charger_id', entity: 'charger' }
    ],
    // A boot is discrete. One run per boot is the useful rule, so `every` is the
    // default; `aggregate` is what catches a charger reboot-looping.
    timing: { kind: 'modes', modes: ['aggregate', 'every'], defaultMode: 'every', sustainedFill: null },
    attributes: orderedAttributes(EVENT_ATTRIBUTES['charger.boot_notification'], ['charger_id', 'charger_name', 'site_id', 'organization_id', 'charge_point_vendor', 'charge_point_model', 'charge_point_serial_number', 'charge_box_serial_number', 'firmware_version', 'iccid', 'imsi', 'meter_type', 'meter_serial_number', 'timestamp'])
  },
  {
    eventType: 'charger.data_transfer',
    scopeDomain: 'charger',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'charger_id', entity: 'charger' }
    ],
    // A vendor data-transfer message is discrete: each one is its own payload,
    // with no value carried between them for a window to hold.
    timing: { kind: 'modes', modes: ['aggregate', 'every'], defaultMode: 'every', sustainedFill: null },
    // The opaque vendor `data` payload is a nested object, so it carries no operator
    // and the generated schema leaves it out — there is nothing to author against.
    attributes: orderedAttributes(EVENT_ATTRIBUTES['charger.data_transfer'], ['charger_id', 'charger_name', 'site_id', 'organization_id', 'vendor_id', 'message_id', 'request_id', 'timestamp'])
  },
  {
    eventType: 'charger.transaction_summary',
    scopeDomain: 'charger',
    // The transaction-summary producer populates `site_id` on every message, so
    // this event scopes broad → narrow: org → site → charger.
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'charger_id', entity: 'charger' }
    ],
    // Each summary message reports one discrete moment of a session — a start, a
    // meter batch, a plug-out, a stop — rather than a reading that persists
    // between them, so `every` (one run per message that matches) and `aggregate`
    // (how many in a window) are the two that mean what they say. Authors reach
    // for this event to act on a finished session, so `every` is the default.
    timing: { kind: 'modes', modes: ['aggregate', 'every'], defaultMode: 'every', sustainedFill: null },
    // The summary is a running snapshot re-published on every OCPP event of a
    // session; `message_type` is the wire discriminator (filter to `StopTransaction`
    // for final session totals). The optional session/metric fields are populated by
    // the adapter whenever the producing event carries them — `stop_*` only on
    // `StopTransaction`, `plug_out_time` only near session end, metrics whenever the
    // summary reports them — so they are marked sparse.
    // SoC rides in from the vehicle and voltage from the meter, so both depend on
    // what the hardware reports rather than on the summary itself.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['charger.transaction_summary'], ['charger_id', 'message_type', 'transaction_id', 'organization_id', 'site_id', 'node_id', 'connector_id', 'ocpp_tag', 'start_timestamp', 'stop_timestamp', 'stop_reason', 'stop_event_actor', 'plug_in_time', 'plug_out_time', 'total_energy_imported', 'total_energy_exported', 'mean_power_import', 'max_power_import', 'latest_power_import', 'starting_soc', 'latest_soc', 'max_voltage', 'min_voltage', 'timestamp']), {
      latest_power_import: { unit: 'kW' },
      latest_soc: { unit: '%', sparse: true },
      max_power_import: { unit: 'kW' },
      max_voltage: { unit: 'V', sparse: true },
      mean_power_import: { unit: 'kW' },
      min_voltage: { unit: 'V', sparse: true },
      plug_in_time: { sparse: true },
      plug_out_time: { sparse: true },
      starting_soc: { unit: '%', sparse: true },
      stop_event_actor: { sparse: true },
      stop_reason: { sparse: true },
      stop_timestamp: { sparse: true },
      total_energy_exported: { unit: 'kWh' },
      total_energy_imported: { unit: 'kWh' }
    })
  },
  {
    eventType: 'vehicle.telemetry',
    scopeDomain: 'vehicle',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' },
      { field: 'vehicle_id', entity: 'vehicle' }
    ],
    // Telemetry is a gauge stream reported per vehicle, continuously: `every`
    // there is one run per vehicle per report, which is the foot-gun the default
    // exists to avoid. `immediate` fires once when a vehicle crosses the
    // threshold and again only after it recovers and crosses back.
    timing: { kind: 'modes', modes: ['immediate', 'sustained', 'aggregate', 'every'], defaultMode: 'immediate', sustainedFill: 'hold_last' },
    // The metric fields are provider-dependent and frequently absent on the wire —
    // state of charge rides on roughly one message in ten — so a condition on one
    // simply won't match telemetry from providers that don't report it. The
    // distance/time-until-home pair is sparser still: an upstream sweep computes it,
    // and only for a vehicle whose home site carries a geofence and coordinates.
    // Range and distance-to-home share the kilometre unit so a condition can compare
    // them directly ("will the vehicle make it home on what is left"). `site_id` is
    // the vehicle's home site — the same value the scope's site filter narrows
    // vehicles by, so the two stay consistent.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['vehicle.telemetry'], ['vehicle_id', 'organization_id', 'fleet_id', 'site_id', 'vehicle_status', 'state_of_charge', 'latitude', 'longitude', 'speed_kph', 'odometer_km', 'remaining_range_km', 'distance_until_home_km', 'time_until_home_minutes', 'source_provider', 'timestamp']), {
      distance_until_home_km: { unit: 'km', sparse: true },
      odometer_km: { unit: 'km', sparse: true },
      remaining_range_km: { unit: 'km', sparse: true },
      speed_kph: { unit: 'kph', sparse: true },
      state_of_charge: { unit: '%', sparse: true },
      time_until_home_minutes: { unit: 'min', sparse: true }
    })
  },
  {
    eventType: 'vehicle.fault',
    scopeDomain: 'vehicle',
    // VehicleFaultAttributes carries no `site_id` (unlike vehicle.telemetry), so
    // the fault event scopes org → vehicle only — there is no site field the
    // backend validator would accept a filter on.
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'vehicle_id', entity: 'vehicle' }
    ],
    // A fault is a discrete report, so there is no contrary reading for
    // `immediate` or `sustained` to resolve on. Telematics re-reports the same
    // fault for as long as it is present, which makes this the noisiest discrete
    // stream in the catalog — hence `aggregate` as the default: "this fault N+
    // times in a window" separates a real problem from a rattling sensor.
    timing: { kind: 'modes', modes: ['aggregate', 'every'], defaultMode: 'aggregate', sustainedFill: null },
    // Fault decode is provider-dependent (Geotab / Zonar): SPN/FMI, source-address,
    // DTC, and lamp fields are populated when the provider reports them and are null
    // otherwise — a condition on an unreported field simply won't match, so they stay
    // available and are marked sparse instead.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['vehicle.fault'], ['vehicle_id', 'organization_id', 'provider', 'spn', 'spn_name', 'spn_description', 'fmi', 'fmi_text', 'source_address', 'source_name', 'source_label', 'occurrence_count', 'mil_lamp_on', 'amber_lamp_on', 'red_lamp_on', 'protect_lamp_on', 'dtc_id', 'dtc_short_code', 'dtc_description', 'latitude', 'longitude', 'timestamp']), {
      amber_lamp_on: { sparse: true },
      dtc_description: { sparse: true },
      dtc_id: { sparse: true },
      dtc_short_code: { sparse: true },
      fmi: { sparse: true },
      fmi_text: { sparse: true },
      mil_lamp_on: { sparse: true },
      protect_lamp_on: { sparse: true },
      red_lamp_on: { sparse: true },
      source_address: { sparse: true },
      source_label: { sparse: true },
      source_name: { sparse: true },
      spn: { sparse: true },
      spn_description: { sparse: true },
      spn_name: { sparse: true }
    })
  },
  {
    eventType: 'der.fault',
    scopeDomain: 'der',
    // Scopes org → site. There is no per-asset picker: a DER fleet is named by
    // where its assets sit. The asset kind is not a scope dimension — the card the
    // trigger was authored from pins it (see {@link DER_FAULT_ASSET_TYPE_FIELD}).
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' }
    ],
    // The feed carries both alarm edges, so unlike the other fault streams a DER
    // fault does report its own clear. `immediate` is still withheld: an asset
    // holds several alarms at once while an episode keys per asset as a
    // last-event-wins level, so one alarm's RAISED would swallow every alarm
    // raised behind it and one alarm's CLEARED would resolve the episode while the
    // asset is still faulted. Per-alarm episode keying is what makes `immediate`
    // offerable here. `sustained` starves regardless — a raised fault emits
    // nothing further for a window to hold across — so what is left is counting:
    // `aggregate` for an alarm storm, and `every` for one run per alarm.
    timing: {
      kind: 'modes',
      modes: ['aggregate', 'every'],
      defaultMode: 'aggregate',
      sustainedFill: null,
      // The family's reason ("nothing would tell the trigger it went back to
      // normal") is false here: the clear arrives, it just cannot be attributed to
      // the asset's whole state.
      unavailableReason: { immediate: 'multiAlarm' }
    },
    // `der_asset_id` is the subject. `alarm_code` / `alarm_reason` are the vendor's
    // own fault text and arrive only when the asset reports them; `location` /
    // `asset_name` only when the asset resolves.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['der.fault'], ['der_asset_id', 'asset_name', 'asset_type', 'organization_id', 'site_id', 'location', 'alarm_code', 'alarm_reason', 'severity', 'transition', 'timestamp']), {
      // The card a fault trigger was authored from pins its asset kind (see
      // `DER_FAULT_ASSET_TYPE_FIELD`), so the field reads as a constant in a
      // message and as a restatement of the trigger in a filter.
      asset_type: { offeredAsCondition: false, offeredAsToken: false },
      // The vendor vocabularies read as prose (a severity scale, an alarm's two
      // edges), unlike e.g. charger.status_notification's lowercase severity.
      severity: { titleCased: true },
      transition: { titleCased: true }
    })
  },
  {
    eventType: CONNECTIVITY_EVENT_TYPE,
    scopeDomain: CONNECTIVITY_SCOPE_VARIANTS.charger.scopeDomain,
    // Scopes broad → narrow: org → site → subject. The org dimension is what lets a
    // parent-org author watch one child's devices; the envelope's own org is an
    // authorization boundary that cannot narrow below the subtree. The subject
    // dimension filters on `entity_id` and its picker depends on the watched kind, so
    // the entry carries the default kind's shape and {@link scopeFieldsForEvent}
    // resolves the rest from {@link CONNECTIVITY_SCOPE_VARIANTS}.
    scopeFields: CONNECTIVITY_SCOPE_VARIANTS.charger.scopeFields,
    // A not-heard-from trigger states its timing as the silence window in its own
    // editor (`NotHeardFromWindowField`), so the Timing panel never renders for
    // it and a firing-mode list would describe a control that isn't there.
    timing: { kind: 'dedicated' },
    // `entity_type` and `active` are decided by the trigger kind rather than the
    // author: the backend ANDs the picked entity kind and the feed's in-service flag
    // into the watch scope during decomposition. Both are therefore constant across
    // every run of a given workflow — `entity_type` is whatever the window editor
    // picked, and `active` is true by construction, since an out-of-service entity
    // never opens one of these notifications — so a token for either reads as a
    // constant the author already knows. The picker offers the four that vary and say
    // something: which entity went quiet, whose it is, where it sits, and when it was
    // last heard from.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['entity.connectivity'], ['entity_type', 'entity_id', 'organization_id', 'site_id', 'last_heard_at', 'active']), {
      active: { offeredAsToken: false },
      entity_type: { offeredAsToken: false }
    })
  },
  // `condition.opened` / `condition.resolved` are deliberately absent: the backend
  // synthesizes them when it decomposes a temporal modifier into a monitor, so they
  // are never authored directly and must not appear in the event picker.
  {
    eventType: 'site.power_utilization',
    scopeDomain: 'site',
    scopeFields: [
      { field: 'organization_id', entity: 'organization' },
      { field: 'site_id', entity: 'site' }
    ],
    // A site reports its draw against the enforced limit every minute, so both
    // readings are gauges and `every` would be a run per site per minute. The
    // signal is a noisy one, so the default absorbs oscillation: a sustained
    // window over the default five minutes, rather than a run on every threshold
    // crossing. `immediate` stays on offer for an author who wants the crossing.
    timing: { kind: 'modes', modes: ['immediate', 'sustained', 'aggregate', 'every'], defaultMode: 'sustained', sustainedFill: 'hold_last' },
    // `site_name` is the site's display name, and it exists so a message can name the
    // site rather than interpolate a UUID. It is the one attribute here that can be
    // null — the producer sends it only when the site resolves — so it is sparse
    //: genuinely carried, just occasionally absent.
    //
    // Both readings are numbers, which is what makes the site power-limit breach
    // authorable: utilization against the ceiling riding on the same sample, compared
    // field-to-field and sustained over a window, and an ordered operator is only
    // valid on a NUMBER.
    attributes: withAttributeMetadata(orderedAttributes(EVENT_ATTRIBUTES['site.power_utilization'], ['site_id', 'site_name', 'organization_id', 'utilization_kw', 'limit_kw']), {
      limit_kw: { unit: 'kW' },
      site_name: { sparse: true },
      utilization_kw: { unit: 'kW' }
    })
  },
  // A scheduled trigger fires on its recurrence, not on an event stream, so there
  // is nothing for a firing mode to modify: the schedule editor states the timing.
  // Its attributes are the fired schedule's own plumbing (which trigger, which
  // workflow version, when it fired), which the schedule editor already states in
  // the author's terms, so the builder offers none of them and the entry carries no
  // schema.
  { eventType: 'scheduled', scopeDomain: 'account', scopeFields: [], timing: { kind: 'none' }, attributes: [] }
  // `as const satisfies` rather than a type annotation: an annotation would widen
  // every attribute `key` to `string`, and those literals are what `AttributeKeyFor`
  // reads so consumers can be checked against this catalog.
] as const satisfies readonly CatalogEventType[]

/**
 * The attribute keys one event declares, as a union of literals.
 *
 * Consumers that must stay in step with this catalog key off this rather than
 * `string`, so omitting an attribute they are required to cover is a compile error
 * naming the key (see `EVENT_ATTRIBUTE_LABEL_KEY`). An event with no verified schema
 * resolves to `never`, which `Record<never, …>` satisfies with an empty object.
 */
export type AttributeKeyFor<E extends EventType> = Extract<
  (typeof EVENT_CATALOG)[number],
  { eventType: E }
>['attributes'][number]['key']

/**
 * The declared members of an `enum` attribute, as a union of literals. The
 * counterpart to {@link AttributeKeyFor} for attributes whose vocabulary is
 * inline on the catalog entry rather than a named export, so a seeded filter can
 * be checked against the values the event can actually carry.
 */
export type AttributeEnumValueFor<E extends EventType, K extends string> = Extract<
  Extract<(typeof EVENT_CATALOG)[number], { eventType: E }>['attributes'][number],
  { key: K; enumValues: readonly string[] }
>['enumValues'][number]

/**
 * The `der.fault` attribute a fresh trigger filters on, and the edge it keeps
 * (see `builderModel.nextTriggerForEvent`). Tied to the catalog so renaming the
 * attribute is a compile error rather than a filter that silently never matches.
 */
export const DER_FAULT_TRANSITION_FIELD = 'transition' satisfies AttributeKeyFor<'der.fault'>
export const DER_FAULT_TRANSITION_RAISED = 'RAISED' satisfies (typeof DER_FAULT_TRANSITIONS)[number]

/**
 * The `der.fault` attribute carrying the asset kind a fault trigger watches.
 *
 * Not a scope field: the author never selects it, the card they picked pins it.
 * It still reaches the wire as a `ONE_OF` leaf on the trigger's `scope` predicate,
 * which is where the backend accepts a filter on a real event attribute — so a
 * battery-fault trigger is, on the wire, a fault trigger narrowed to `BESS`.
 */
export const DER_FAULT_ASSET_TYPE_FIELD = 'asset_type' satisfies AttributeKeyFor<'der.fault'>

/**
 * The attributes the remaining seeded triggers filter on (see
 * `builderModel.defaultConditionForEvent`), tied to the catalog for the same
 * reason as the `der.fault` pair above.
 *
 * These four exist because their events default to a firing mode that watches a
 * condition — `immediate` or `sustained` — and such a mode without a condition
 * is invalid authoring. Each seed is the intent the event's own `timing` comment
 * names as the reason for that default, so the two stay in step: change the
 * default mode and the seed has to change with it.
 */
export const CHARGER_STATUS_FIELD = 'status' satisfies AttributeKeyFor<'charger.status_notification'>
export const CHARGER_STATUS_FAULTED = 'Faulted' satisfies AttributeEnumValueFor<
  'charger.status_notification',
  'status'
>
export const VEHICLE_STATE_OF_CHARGE_FIELD = 'state_of_charge' satisfies AttributeKeyFor<'vehicle.telemetry'>
export const SITE_UTILIZATION_FIELD = 'utilization_kw' satisfies AttributeKeyFor<'site.power_utilization'>
export const SITE_LIMIT_FIELD = 'limit_kw' satisfies AttributeKeyFor<'site.power_utilization'>

// ---- Entity context ------------------------------------------------------

/**
 * How reliably the executor reaches one related entity from a triggering event.
 *
 * `always` — the event carries what identifies the entity on every message, so the
 * fields are there whenever the workflow runs. `conditional` — the entity hangs off
 * a key the event may arrive without: a fault raised with no session in progress has
 * no transaction, and a session authorized by a user tag has no vehicle. Those
 * resolve as legitimately absent and every field reads null, which is what the
 * builder marks so an author sees it before writing the condition rather than after
 * the run.
 */
export type CatalogContextResolution = 'always' | 'conditional'

/** One referenceable field of a resolved entity, in the same shape as an attribute. */
export type CatalogContextField = {
  /** Canonical field key used for matching/serialization; never localized. */
  key: string
  type: CatalogFieldType
  /** Allowed values for `enum` fields, in the order the backend declares them. */
  enumValues?: readonly string[]
}

/** One entity a triggering event can reach, with everything it carries. */
export type CatalogContextEntity = {
  resolution: CatalogContextResolution
  fields: readonly CatalogContextField[]
}

/**
 * The entities one event type reaches, keyed by entity kind. An index signature
 * rather than `Record<ContextEntityKind, …>`: the kinds are themselves read back off
 * the generated literal (see {@link ContextEntityKind}), and most events reach only
 * some of them.
 */
export type CatalogContextEntities = { readonly [kind: string]: CatalogContextEntity }

/**
 * Every entity kind any event reaches, as a union of literals, read off the
 * generated schema rather than restated. `keyof` over the mapped type would
 * intersect the per-event key sets; indexing the mapped type by `EventType` unions
 * them, and an event with no reachable entity contributes `keyof {}` — `never`.
 */
export type ContextEntityKind = { [E in EventType]: keyof (typeof EVENT_CONTEXT)[E] }[EventType]

/** The generated block for one entity kind, across every event that reaches it. */
type GeneratedContextEntity<K extends ContextEntityKind> = {
  [E in EventType]: K extends keyof (typeof EVENT_CONTEXT)[E] ? (typeof EVENT_CONTEXT)[E][K] : never
}[EventType]

/**
 * The field keys one entity kind declares, as a union of literals — the context
 * counterpart to {@link AttributeKeyFor}. Keyed by entity rather than by event,
 * because a resolved entity is the same entity whichever event reached it: the
 * charger view yields the same eleven fields on a boot notification and on a status
 * notification, so its labels are authored once (see `CONTEXT_FIELD_LABEL_KEY`).
 */
export type ContextFieldKeyFor<K extends ContextEntityKind> = GeneratedContextEntity<K>['fields'][number]['key']

/**
 * The declared members of one entity's `enum` field, as a union of literals — the
 * context counterpart to {@link AttributeEnumValueFor}, so a label map for the
 * field must name exactly the values the backend can resolve.
 */
export type ContextEnumValueFor<K extends ContextEntityKind, F extends ContextFieldKeyFor<K>> = Extract<
  GeneratedContextEntity<K>['fields'][number],
  { key: F; enumValues: readonly string[] }
>['enumValues'][number]

/**
 * The templating / condition-field prefix for live entity data.
 *
 * Unlike an event attribute, a context path is never shortened: the editor, the
 * wire `Condition.field` and a `{{…}}` reference all spell it `context.<entity>.<field>`,
 * so the serializer has nothing to strip or re-add and a persisted path round-trips
 * untouched.
 */
export const CONTEXT_PREFIX = 'context.'

/** An entity kind and one of its field keys → the path both surfaces carry. */
export function contextFieldPath(entity: string, field: string): string {
  return `${CONTEXT_PREFIX}${entity}.${field}`
}

/**
 * A `context.*` path split into the entity it starts from and the rest of the path,
 * or `undefined` when it is not a context reference at all. The remainder is left
 * whole: a flat spelling is one segment (`state_of_charge`), while a chained one —
 * which the backend also accepts but the picker never writes — carries the hops too
 * (`vehicle.state_of_charge`).
 */
export function parseContextPath(path: string): { entity: string; rest: string } | undefined {
  if (!path.startsWith(CONTEXT_PREFIX)) return undefined
  const remainder = path.slice(CONTEXT_PREFIX.length)
  const separator = remainder.indexOf('.')
  if (separator <= 0 || separator === remainder.length - 1) return undefined
  return { entity: remainder.slice(0, separator), rest: remainder.slice(separator + 1) }
}

/**
 * The order an author reads one entity's fields in, most reached-for first.
 *
 * The artifact sorts fields by their backend key, which is not the order they
 * render in and not an order anyone reads: `num_chargers, num_vehicles,
 * power_ceiling, site_id, site_name` comes out as "Charger count, Vehicle count,
 * Power ceiling, ID, Name" — alphabetical in a spelling the picker never shows.
 *
 * The rule here is what an author reaches for: what they would put in a message or
 * test in a condition comes first (a name, a status, a reading), and the
 * identifiers that only matter to another system come last. Editorial, like the
 * per-event attribute orders above, and kept per entity kind rather than per event
 * because a resolved entity is the same entity whichever event reached it.
 *
 * Must name every field of its entity exactly once — {@link orderedAttributes}
 * throws at module load otherwise, so a field the backend adds is placed here
 * deliberately rather than appearing wherever the artifact happens to sort it.
 */
const CONTEXT_FIELD_ORDER = {
  charger: [
    'charger_name',
    'status',
    'operational_status',
    'connectivity_status',
    'last_heard_at',
    'max_power',
    'vendor',
    'model',
    'firmware_version',
    'site_id',
    'charger_id'
  ],
  connector: ['connector_name', 'evse_id', 'connector_id', 'charger_id'],
  site: [
    'site_name',
    'address',
    'power_ceiling',
    'num_chargers',
    'num_vehicles',
    'timezone',
    'site_id',
    'latitude',
    'longitude'
  ],
  transaction: [
    'total_energy_imported',
    'start_timestamp',
    'stop_timestamp',
    'latest_soc',
    'starting_soc',
    'mean_power_import',
    'max_power_import',
    'total_energy_exported',
    'plug_in_time',
    'plug_out_time',
    'connector_id',
    'ocpp_tag',
    'transaction_id',
    'transaction_pk',
    'vehicle_id',
    'charger_id',
    'site_id'
  ],
  vehicle: [
    'vehicle_name',
    'vehicle_status',
    'state_of_charge',
    'remaining_range',
    'odometer',
    'latitude',
    'longitude',
    'home_site_id',
    'vehicle_id',
    'organization_id'
  ],
  bess: [
    'bess_name',
    'run_state',
    'state_of_charge',
    'state_of_health',
    'available_discharge_energy',
    'available_charge_energy',
    'energy_capacity',
    'discharge_power_limit',
    'charge_power_limit',
    'max_power',
    'control_mode',
    'grid_mode',
    'last_heard_at',
    'manufacturer',
    'model',
    'firmware_version',
    'site_id',
    'bess_id'
  ],
  generator: [
    'generator_name',
    'run_state',
    'power_output',
    'rated_power',
    'rated_apparent_power',
    'max_power',
    'fuel_type',
    'control_mode',
    'grid_mode',
    'last_heard_at',
    'manufacturer',
    'model',
    'firmware_version',
    'site_id',
    'generator_id'
  ],
  link: ['name', 'nickname', 'connectivity_status', 'last_heard_at', 'description', 'site_id', 'link_id']
} as const satisfies { [K in ContextEntityKind]: readonly ContextFieldKeyFor<K>[] }

/**
 * The live entity each asset kind resolves, on the events that reach one entity per
 * kind: a DER fault, keyed by the event's `asset_type`, and the connectivity feed,
 * keyed by its `entity_type`.
 *
 * Each such entity is conditional on that attribute — the executor resolves
 * whichever one the event's asset is. A trigger card pins the kind, so on a
 * battery-fault trigger the generator can never resolve and the BESS always does:
 * the picker offers the one entity the trigger can reach, and says nothing about its
 * availability.
 */
const PINNED_CONTEXT_ENTITY: { readonly [E in EventType]?: Readonly<Record<string, ContextEntityKind>> } = {
  [DER_FAULT_EVENT_TYPE]: {
    BESS: 'bess',
    GENSET: 'generator'
  } satisfies Record<DerFaultAssetType, ContextEntityKind>,
  [CONNECTIVITY_EVENT_TYPE]: {
    charger: 'charger',
    synoplink: 'link'
  } satisfies Record<ConnectivityEntityKind, ContextEntityKind>
}

/**
 * The entities a trigger reaches, which is its event's — narrowed, on a card that
 * pins an asset kind (see {@link PINNED_CONTEXT_ENTITY}), to that kind's entity. A
 * hydrated trigger pinning no kind the table knows keeps every entity, each still
 * conditional on the event's asset kind.
 */
export function contextEntitiesForTrigger(
  eventType: EventType | null | undefined,
  assetKind: string | undefined
): readonly ContextEntityEntry[] {
  const entities = contextEntitiesForEvent(eventType)
  const byKind = eventType ? PINNED_CONTEXT_ENTITY[eventType] : undefined
  const pinned = byKind && assetKind !== undefined && Object.hasOwn(byKind, assetKind) ? byKind[assetKind] : undefined
  if (!byKind || !pinned) return entities
  const others: ReadonlySet<ContextEntityKind> = new Set(Object.values(byKind).filter((kind) => kind !== pinned))
  return entities
    .filter((entity) => !others.has(entity.kind))
    .map((entity) => (entity.kind === pinned ? { ...entity, resolution: 'always' as const } : entity))
}

/**
 * The entities a trigger of this event type resolves, in the artifact's order (by
 * entity name), each with its fields in the order an author reads them. Empty for an
 * event with no reachable entity, and for one the catalog doesn't describe.
 */
export function contextEntitiesForEvent(eventType: EventType | null | undefined): readonly ContextEntityEntry[] {
  if (!eventType) return []
  return ORDERED_EVENT_CONTEXT[eventType] ?? []
}

/** One reachable entity, with the kind that keys it folded in for iteration. */
export type ContextEntityEntry = CatalogContextEntity & { kind: ContextEntityKind }

/**
 * Every event's context, ordered once at module load — so a field the backend adds
 * without a place in {@link CONTEXT_FIELD_ORDER} throws when the module is first
 * imported, rather than on the render that happens to open the picker.
 */
const ORDERED_EVENT_CONTEXT: Record<string, readonly ContextEntityEntry[]> = Object.fromEntries(
  Object.entries(EVENT_CONTEXT as Record<string, CatalogContextEntities>).map(([eventType, entities]) => [
    eventType,
    Object.entries(entities).map(([kind, entity]) => ({
      kind: kind as ContextEntityKind,
      resolution: entity.resolution,
      fields: orderedAttributes(entity.fields, CONTEXT_FIELD_ORDER[kind as ContextEntityKind])
    }))
  ])
)

/**
 * The entity and field a `context.*` path yields on a trigger of this event type,
 * or `undefined` when it yields nothing the catalog knows of. `assetKind` is the
 * kind the trigger pins; an entity that kind rules out yields nothing, so a charger
 * field on a link-not-heard-from trigger reads as unknown (see
 * {@link contextEntitiesForTrigger}).
 *
 * A path's last two segments name what it resolves to, whether it is the flat
 * spelling the picker writes (`context.vehicle.state_of_charge`) or the chained one
 * the backend also accepts (`context.transaction.vehicle.state_of_charge` — the same
 * fact, reached the long way). Both therefore resolve to the vehicle's
 * `state_of_charge`, which is what lets one rule label a chip and judge a reference.
 *
 * The entity a chain starts from has to be reachable too, so a chain off an
 * unreachable entity resolves to nothing. The hops between are not checked: the flat
 * catalog describes which entities an event reaches, not which entity reaches which,
 * so a chain whose hop does not exist resolves here and is left to the backend
 * validator. That errs toward accepting a reference the backend may reject, which is
 * the safe direction — the alternative paints a reference that runs fine as broken.
 */
export function contextFieldForPath(
  eventType: EventType | null | undefined,
  path: string,
  assetKind?: string
): { entity: ContextEntityEntry; field: CatalogContextField } | undefined {
  const parsed = parseContextPath(path)
  if (!parsed) return undefined
  const entities = contextEntitiesForTrigger(eventType, assetKind)
  if (!entities.some((candidate) => candidate.kind === parsed.entity)) return undefined
  const segments = parsed.rest.split('.')
  const key = segments[segments.length - 1]!
  const kind = segments.length > 1 ? segments[segments.length - 2]! : parsed.entity
  const entity = entities.find((candidate) => candidate.kind === kind)
  const field = entity?.fields.find((candidate) => candidate.key === key)
  return entity && field ? { entity, field } : undefined
}

/**
 * Whether a `context.*` path is one a trigger of this event type, pinning
 * `assetKind`, can resolve — the same judgement {@link contextFieldForPath} makes, so
 * a reference the editor labels is a reference the editor accepts, and a typo'd field
 * is caught either way.
 */
export function isKnownContextPath(
  eventType: EventType | null | undefined,
  path: string,
  assetKind?: string
): boolean {
  return contextFieldForPath(eventType, path, assetKind) !== undefined
}

// ---- Trigger picker entries ---------------------------------------------

/**
 * Every valid leaf key under the `workflows.eventCatalog` namespace — composed
 * from the same `use-intl` utilities (`MessageKeys`/`NestedKeyOf`/
 * `NestedValueOf`) that `useTranslations` itself uses internally to type `t`.
 * Maps whose values are keys in that namespace type them against this rather
 * than `string`, so a typo'd or renamed key is a compile error where the map is
 * written and `t(key)` needs no cast.
 */
export type EventCatalogMessageKey = MessageKeys<
  NestedValueOf<Messages, 'workflows.eventCatalog'>,
  NestedKeyOf<NestedValueOf<Messages, 'workflows.eventCatalog'>>
>

type TriggerPickerEntryBase = {
  /**
   * Stable identity for the card — its React key, its picker glyph, and how a
   * persisted trigger is matched back to it. Independent of the event type,
   * because one event can stand behind several cards.
   */
  key: string
  category: TriggerPickerCategory
  /** Title shown on the card, on the canvas node, and in the inspector header. */
  labelKey: EventCatalogMessageKey
  /** One-line description shown under the title. */
  descriptionKey: EventCatalogMessageKey
}

/**
 * One card in the trigger picker.
 *
 * A card is what an author picks, and it pins everything the picked trigger
 * needs that the author is not asked for again. Most events offer exactly one
 * card, so the card and the event are the same choice. Two feeds are the
 * exception, for the same reason: each reports on several kinds of asset while a
 * trigger watches exactly one, so each kind gets its own card and the choice of
 * card is the choice of kind. The connectivity feed splits by entity kind
 * (`entityType`), the fault feed by DER asset kind (`assetType`); both arms
 * require their kind, because such a card naming none would leave the trigger
 * watching everything the feed reports on, with no other control to narrow it.
 */
export type TriggerPickerEntry =
  | (TriggerPickerEntryBase & {
      eventType: Exclude<EventType, typeof CONNECTIVITY_EVENT_TYPE | typeof DER_FAULT_EVENT_TYPE>
      entityType?: never
      assetType?: never
    })
  | (TriggerPickerEntryBase & {
      eventType: typeof CONNECTIVITY_EVENT_TYPE
      /** The entity kind this card's trigger watches, and the scope shape behind it. */
      entityType: ConnectivityEntityKind
      assetType?: never
    })
  | (TriggerPickerEntryBase & {
      eventType: typeof DER_FAULT_EVENT_TYPE
      entityType?: never
      /** The DER asset kind this card's trigger watches. */
      assetType: DerFaultAssetType
    })

/**
 * The trigger picker's cards, grouped into sections by {@link CATEGORY_ORDER}.
 *
 * A card whose `eventType` is not in {@link SUPPORTED_EVENT_TYPES} renders
 * disabled ("coming soon"), though today every card is supported. The two
 * not-heard-from cards, and likewise the two DER fault cards, author the same
 * feed against the same schema; they differ only in the kind they pin, and each
 * sits in the section of the assets it watches.
 */
export const TRIGGER_PICKER_ENTRIES = [
  {
    key: 'charger.status_notification',
    eventType: 'charger.status_notification',
    category: 'charger',
    labelKey: 'eventType_chargerStatusNotification',
    descriptionKey: 'eventTypeDescription_chargerStatusNotification'
  },
  {
    key: 'charger.start_transaction',
    eventType: 'charger.start_transaction',
    category: 'charger',
    labelKey: 'eventType_chargerStartTransaction',
    descriptionKey: 'eventTypeDescription_chargerStartTransaction'
  },
  {
    key: 'charger.stop_transaction',
    eventType: 'charger.stop_transaction',
    category: 'charger',
    labelKey: 'eventType_chargerStopTransaction',
    descriptionKey: 'eventTypeDescription_chargerStopTransaction'
  },
  {
    key: 'charger.boot_notification',
    eventType: 'charger.boot_notification',
    category: 'charger',
    labelKey: 'eventType_chargerBootNotification',
    descriptionKey: 'eventTypeDescription_chargerBootNotification'
  },
  {
    key: 'charger.data_transfer',
    eventType: 'charger.data_transfer',
    category: 'charger',
    labelKey: 'eventType_chargerDataTransfer',
    descriptionKey: 'eventTypeDescription_chargerDataTransfer'
  },
  {
    key: 'charger.transaction_summary',
    eventType: 'charger.transaction_summary',
    category: 'charger',
    labelKey: 'eventType_chargerTransactionSummary',
    descriptionKey: 'eventTypeDescription_chargerTransactionSummary'
  },
  {
    key: 'notHeardFrom.charger',
    eventType: CONNECTIVITY_EVENT_TYPE,
    entityType: 'charger',
    category: 'charger',
    labelKey: 'eventType_chargerNotHeardFrom',
    descriptionKey: 'eventTypeDescription_chargerNotHeardFrom'
  },
  {
    key: 'site.power_utilization',
    eventType: 'site.power_utilization',
    category: 'site',
    labelKey: 'eventType_sitePowerUtilization',
    descriptionKey: 'eventTypeDescription_sitePowerUtilization'
  },
  {
    key: 'vehicle.telemetry',
    eventType: 'vehicle.telemetry',
    category: 'vehicle',
    labelKey: 'eventType_vehicleTelemetry',
    descriptionKey: 'eventTypeDescription_vehicleTelemetry'
  },
  {
    key: 'vehicle.fault',
    eventType: 'vehicle.fault',
    category: 'vehicle',
    labelKey: 'eventType_vehicleFault',
    descriptionKey: 'eventTypeDescription_vehicleFault'
  },
  {
    key: 'notHeardFrom.synoplink',
    eventType: CONNECTIVITY_EVENT_TYPE,
    entityType: 'synoplink',
    category: 'synoplink',
    labelKey: 'eventType_synopLinkNotHeardFrom',
    descriptionKey: 'eventTypeDescription_synopLinkNotHeardFrom'
  },
  {
    key: 'der.fault.bess',
    eventType: DER_FAULT_EVENT_TYPE,
    assetType: 'BESS',
    category: 'der',
    labelKey: 'eventType_bessFault',
    descriptionKey: 'eventTypeDescription_bessFault'
  },
  {
    key: 'der.fault.genset',
    eventType: DER_FAULT_EVENT_TYPE,
    assetType: 'GENSET',
    category: 'der',
    labelKey: 'eventType_generatorFault',
    descriptionKey: 'eventTypeDescription_generatorFault'
  },
  {
    key: 'scheduled',
    eventType: 'scheduled',
    category: 'schedule',
    labelKey: 'eventType_scheduled',
    descriptionKey: 'eventTypeDescription_scheduled'
  }
  // `as const satisfies` rather than a type annotation: an annotation would widen
  // every `key` to `string`, and those literals are what `TriggerPickerEntryKey`
  // reads so the picker's glyph map can be checked against this list.
] as const satisfies readonly TriggerPickerEntry[]

/**
 * One card as this module declares it — narrower than {@link TriggerPickerEntry},
 * whose `key` is a bare string. Preserving the literal is what lets a card index
 * a per-card map — the picker's glyphs, its "compare with" pointers — without a
 * cast, so a card missing from such a map is a compile error naming it.
 */
export type CatalogTriggerPickerEntry = (typeof TRIGGER_PICKER_ENTRIES)[number]

/** The identity of one picker card, as a union of literals. */
export type TriggerPickerEntryKey = CatalogTriggerPickerEntry['key']

/**
 * The parts of a trigger that decide which card authored it: the event it fires
 * on, plus the kind it watches where the event's cards split by one — the entity
 * kind for the connectivity feed, the asset kind for the fault feed. Both the
 * builder's `TriggerForm` and a persisted wire trigger reduce to this.
 *
 * `assetTypes` is a list because it is read off a filter that can name any number
 * of kinds, not because a card pins more than one.
 */
export type TriggerPickerSubject = {
  eventType: EventType | null | undefined
  entityType?: string
  assetTypes?: readonly string[]
}

/**
 * The card a trigger was authored from, which is where its title and description
 * come from.
 *
 * Undefined when no card describes the trigger: an event the catalog does not
 * list (a synthesized `condition.*` trigger, say), a persisted connectivity kind
 * outside {@link CONNECTIVITY_ENTITY_KINDS}, since the feed's value space grows
 * server-side, or a fault trigger whose `asset_type` filter names anything other
 * than one carded kind — the filter is a list, and the picker is not the only
 * thing that writes one. Callers fall back to the event's own label for those.
 */
export function pickerEntryFor(trigger: TriggerPickerSubject): TriggerPickerEntry | undefined {
  return TRIGGER_PICKER_ENTRIES.find((entry) => {
    if (entry.eventType !== trigger.eventType) return false
    if (entry.eventType === CONNECTIVITY_EVENT_TYPE) return entry.entityType === trigger.entityType
    if (entry.eventType === DER_FAULT_EVENT_TYPE) {
      const assetTypes = trigger.assetTypes ?? []
      return assetTypes.length === 1 && assetTypes[0] === entry.assetType
    }
    return true
  })
}

// ---- Lookups & applicability -------------------------------------------

export function getEventType(eventType: EventType | null | undefined): CatalogEventType | undefined {
  return EVENT_CATALOG.find((event) => event.eventType === eventType)
}

export function getAttribute(event: CatalogEventType, key: string): CatalogAttribute | undefined {
  return event.attributes.find((attribute) => attribute.key === key)
}

/**
 * Whether the condition editor offers an attribute to filter on — declined for a
 * field whose value the trigger fixes for the whole workflow
 * ({@link CatalogAttribute.offeredAsCondition}).
 */
export function isOfferedAsCondition(attribute: CatalogAttribute): boolean {
  return attribute.offeredAsCondition !== false
}

/**
 * The timing surface of an event type (see {@link CatalogTiming}), or `null` for
 * an event the catalog does not describe — a hydrated non-catalog trigger such as
 * a synthesized `condition.*` event.
 */
export function timingForEvent(eventType: EventType | null | undefined): CatalogTiming | null {
  return getEventType(eventType)?.timing ?? null
}

/**
 * The firing modes offered for an event type. An event the catalog doesn't
 * describe falls back to the full set: no mode can be proven wrong there, and
 * hiding modes from a trigger the editor can still render would be a guess. An
 * event whose timing lives in its own editor offers none.
 */
export function firingModesForEvent(eventType: EventType | null | undefined): readonly CatalogFiringMode[] {
  const timing = timingForEvent(eventType)
  if (!timing) return FIRING_MODE_ORDER
  return timing.kind === 'modes' ? timing.modes : []
}

/**
 * The firing mode a newly picked trigger on this event starts on, or `null` when
 * the event's timing is authored elsewhere. An event outside the catalog gets
 * `every` — the mode that needs no monitor and makes no claim about the signal.
 */
export function defaultFiringModeForEvent(eventType: EventType | null | undefined): CatalogFiringMode | null {
  const event = getEventType(eventType)
  if (!event) return 'every'
  return event.timing.kind === 'modes' ? event.timing.defaultMode : null
}

/**
 * The gap policy a freshly selected `sustained` window starts on for this event.
 * Falls back to `hold_last`, matching the mode's own meaning: `sustained` is only
 * offered on a signal whose last reading stands until the next one arrives.
 */
export function defaultSustainedFillForEvent(eventType: EventType | null | undefined): FillPolicy {
  const timing = timingForEvent(eventType)
  return (timing?.kind === 'modes' ? timing.sustainedFill : null) ?? 'hold_last'
}

/**
 * The attributes the template token menu offers: populated by ingestion, and
 * worth putting in front of an author (see {@link CatalogAttribute.offeredAsToken}).
 * Narrower than what a template may legally reference — every declared attribute
 * resolves at run time, and both `findUnknownReferences` and `useTokenResolver`
 * work off the full list so a reference typed by hand or authored through the API
 * neither warns nor renders as a broken chip.
 */
export function tokenAttributes(event: CatalogEventType): readonly CatalogAttribute[] {
  return event.attributes.filter((attribute) => attribute.offeredAsToken !== false)
}

/**
 * The control-plane / templating prefix that namespaces a bare attribute key
 * into a condition `field` path. The catalog (and the condition editor) work in
 * bare keys (`status`); the backend `Condition.field` and the templating layer
 * use the full dotted path `event.attributes.status` (`resolve_field` splits on
 * `.`). The serializer bridge converts between the two at its single seam.
 */
export const EVENT_ATTRIBUTE_PREFIX = 'event.attributes.'

/** A bare catalog attribute key → its full control-plane condition `field` path. */
export function attributeFieldPath(key: string): string {
  return key.startsWith(EVENT_ATTRIBUTE_PREFIX) ? key : `${EVENT_ATTRIBUTE_PREFIX}${key}`
}

/**
 * A control-plane condition `field` path → the bare catalog attribute key.
 * Returns the input unchanged when it is not an `event.attributes.*` reference
 * (e.g. an envelope field or a `steps.*` result the editor can't represent), so
 * such a field simply fails to match any catalog attribute downstream.
 */
export function attributeKeyFromField(field: string): string {
  return field.startsWith(EVENT_ATTRIBUTE_PREFIX) ? field.slice(EVENT_ATTRIBUTE_PREFIX.length) : field
}

/**
 * The scope fields the panel can filter an event by (org → site → asset). Empty
 * for unknown events or those whose attribute schema isn't verified yet, in
 * which case the scope panel renders no entity filters (only its "no filters yet"
 * message) since there is no field it can prove valid to scope on.
 *
 * `connectivityEntityKind` names the kind a not-heard-from trigger watches. The
 * connectivity feed's `entity_id` is a different asset per kind, so its shape is
 * resolved from {@link CONNECTIVITY_SCOPE_VARIANTS}; every other event ignores it,
 * as does a kind the catalog has no variant for.
 */
export function scopeFieldsForEvent(
  eventType: EventType | null,
  connectivityEntityKind?: string
): readonly CatalogScopeField[] {
  if (
    eventType === CONNECTIVITY_EVENT_TYPE &&
    connectivityEntityKind !== undefined &&
    isConnectivityEntityKind(connectivityEntityKind)
  ) {
    return CONNECTIVITY_SCOPE_VARIANTS[connectivityEntityKind].scopeFields
  }
  return getEventType(eventType)?.scopeFields ?? []
}

/** Whether an event type exposes a scope field for the given entity kind. */
export function hasScopeEntity(
  eventType: EventType | null,
  entity: ScopeEntityKind,
  connectivityEntityKind?: string
): boolean {
  return scopeFieldsForEvent(eventType, connectivityEntityKind).some((scopeField) => scopeField.entity === entity)
}

/** Operators offered for a given field type. */
export function operatorsForFieldType(type: CatalogFieldType): readonly CatalogOperator[] {
  return OPERATORS_BY_FIELD_TYPE[type]
}

/** Operators offered for a concrete attribute. */
export function operatorsForAttribute(attribute: CatalogAttribute): readonly CatalogOperator[] {
  return operatorsForFieldType(attribute.type)
}

/** Whether an operator is allowed on a field type (the core applicability rule). */
export function isOperatorValidForFieldType(operator: CatalogOperator, type: CatalogFieldType): boolean {
  return OPERATORS_BY_FIELD_TYPE[type].includes(operator)
}

/**
 * Whether a value of one field type can be compared against a value of another —
 * the rule behind a field-vs-field condition, mirroring the backend's
 * `_types_comparable`. `string` and `enum` are interchangeable because both ride
 * the wire as strings; every other type compares only against itself.
 *
 * The backend additionally excludes `object`, which has no {@link CatalogFieldType}
 * analogue — a step-result field is narrowed to a scalar (see
 * {@link isScalarResultField}) before it can be offered as either side.
 */
export function areFieldTypesComparable(left: CatalogFieldType, right: CatalogFieldType): boolean {
  const isStringLike = (type: CatalogFieldType) => type === 'string' || type === 'enum'
  if (isStringLike(left) && isStringLike(right)) return true
  return left === right
}

// ---- Whole-value template references -----------------------------------

/**
 * Path grammar for a `{{…}}` reference, matching `TEMPLATE_REFERENCE` in the
 * backend's `workflows_core/templating.py`. Anchored, so it only matches a value
 * that is *nothing but* one reference.
 */
const WHOLE_REFERENCE = /^\{\{\s*([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)\s*\}\}$/

/**
 * The context path a value points at when the value is *exactly* one `{{…}}`
 * reference, else `undefined` — the value is then an authored literal.
 *
 * Mirrors the backend's `value_reference_path`. Only a whole-value reference
 * counts, and that is precisely what keeps the two forms unambiguous: a braced
 * string mixed with other text is literal text, and every non-string value is a
 * literal by construction. Matching the backend's grammar here means a value it
 * reads as a reference is the same one we do — the alternative is an editor that
 * shows a literal where the runtime sees a field lookup.
 *
 * Distinct from `extractReferences` in `inspector/templating.ts`, which finds
 * every reference embedded *within* prose (a templated email body); this asks
 * whether the whole value is one.
 */
export function wholeReferencePath(value: string): string | undefined {
  return WHOLE_REFERENCE.exec(value)?.[1]
}

/** A context path → the `{{…}}` reference form the wire carries. */
export function referenceToken(path: string): string {
  return `{{${path}}}`
}

// ---- Action result catalog ----------------------------------------------

/** The `action` discriminant of an action step — every built-in action id. */
export type ActionId = ActionStepDefinition['action']

/**
 * Value kind of an action result field. Extends the condition {@link CatalogFieldType}
 * scalars with the aggregate kinds a result can carry (`list`/`object`). Only
 * scalars can be interpolated into a message or bound to a scalar parameter; the
 * token palette hides `list`/`object` results (they'd blow up string interpolation —
 * "only scalar values can be interpolated …"). The backend validator, however,
 * accepts any *declared* field, so {@link isKnownResultField} does not filter by
 * kind.
 */
export type ActionResultFieldType = CatalogFieldType | 'list' | 'object'

/** Field kinds that can be interpolated / bound as a whole scalar value. */
const SCALAR_RESULT_TYPES: ReadonlySet<ActionResultFieldType> = new Set<ActionResultFieldType>([
  'string',
  'number',
  'boolean',
  'enum',
  'datetime'
])

export function isScalarResultField(type: ActionResultFieldType): type is CatalogFieldType {
  return SCALAR_RESULT_TYPES.has(type)
}

/**
 * An `enum` result field together with the members it can take, so a condition
 * on it offers a closed selector. `TValue` is the generated result field's own
 * literal union, which keeps every listed member a real one.
 */
export type ActionResultEnumField<TValue extends string = string> = {
  type: 'enum'
  enumValues: readonly TValue[]
}

/** One leaf of a result schema: a bare value kind, or an `enum` with its members. */
export type ActionResultLeafSchema = ActionResultFieldType | ActionResultEnumField

/**
 * Every declared field of a result model, mapped to its value kind. An enum
 * field may list its members, typed against the generated field so a member the
 * backend does not declare is a compile error.
 */
type ResultFieldTypes<TResult> = {
  [K in keyof TResult]-?: ActionResultFieldType | ActionResultEnumField<Extract<NonNullable<TResult[K]>, string>>
}

/**
 * One entry of a result schema: a leaf, or one level of named sub-fields for a
 * result that nests a curated payload model (`fetch.data`'s per-entity groups).
 * One level is the contract's actual depth — the backend's curated `Fetched*`
 * models are flat — so the walk helpers below don't recurse.
 */
export type ActionResultFieldSchema = ActionResultLeafSchema | Record<string, ActionResultLeafSchema>

/**
 * Whether a schema entry is an enum leaf with members. A nested group is a plain
 * map of leaves, so its `enumValues` (were a sub-field named that) is never an
 * array.
 */
export function isEnumResultField(schema: ActionResultFieldSchema): schema is ActionResultEnumField {
  return typeof schema === 'object' && schema.type === 'enum' && Array.isArray(schema.enumValues)
}

/** Whether a schema entry is a single value rather than a group of sub-fields. */
function isResultLeaf(schema: ActionResultFieldSchema): schema is ActionResultLeafSchema {
  return typeof schema === 'string' || isEnumResultField(schema)
}

function leafType(schema: ActionResultLeafSchema): ActionResultFieldType {
  return typeof schema === 'string' ? schema : schema.type
}

/**
 * The members of a generated string-literal union, in the order listed. Taking
 * them as the keys of a `Record<T, true>` makes a missing or unknown member a
 * compile error, so the list cannot drift from the generated type.
 */
function unionMembers<T extends string>(members: Record<T, true>): readonly T[] {
  return Object.keys(members) as T[]
}

type ChargerCommandCommunicationResult = NonNullable<ChargerRemoteStartResult['communication_result']>

const CHARGER_COMMAND_COMMUNICATION_RESULTS = unionMembers<ChargerCommandCommunicationResult>({
  Applied: true,
  Invalid: true,
  NotConnected: true,
  NotSupported: true,
  Pending: true,
  Rejected: true,
  Timeout: true,
  Unknown: true
})

const RESULT_REPORT_FORMATS = unionMembers<ReportFormat>({ csv: true, xlsx: true })

/**
 * Result-field schema per action, hand-synced to the backend action catalog
 * (`workflows_core.catalog.actions` — the `*Result` model for each action). The
 * keys are the canonical snake_case result field names the backend templating
 * layer resolves and the validator checks references against, as
 * `steps.<id>.result.<field>`. The per-entry `satisfies ResultFieldTypes<…>` ties
 * each map to the generated client result type, so a renamed/removed backend
 * field is a compile error here; the outer `Record<ActionId, …>` annotation makes
 * a newly added action a compile error until its result schema is registered.
 *
 * `wait` produces no result (`WaitResult` has no fields), so nothing on it is
 * addressable — an empty map, matching the backend.
 *
 * Hand-authored, and unlike the event attribute schema above this map is genuinely
 * tied to the generated types: the per-entry `satisfies` is what makes a renamed or
 * removed backend result field a compile error here. A runtime-fetched schema could
 * not be checked that way.
 */
export const ACTION_RESULT_CATALOG: Record<ActionId, Record<string, ActionResultFieldSchema>> = {
  'email.users': { sent: 'list', skipped: 'list' } satisfies ResultFieldTypes<SendEmailResult>,
  'sms.send': { sent: 'list', skipped: 'list' } satisfies ResultFieldTypes<SendSmsResult>,
  'charger.reset': {
    charger_id: 'string',
    applied: 'boolean',
    status_code: 'number'
  } satisfies ResultFieldTypes<ChargerResetResult>,
  'charger.remote_start': {
    charger_id: 'string',
    connector_id: 'number',
    tag_id: 'string',
    transaction_id: 'string',
    communication_result: { type: 'enum', enumValues: CHARGER_COMMAND_COMMUNICATION_RESULTS },
    status_code: 'number'
  } satisfies ResultFieldTypes<ChargerRemoteStartResult>,
  wait: {} satisfies ResultFieldTypes<WaitResult>,
  'report.run': {
    report_format: { type: 'enum', enumValues: RESULT_REPORT_FORMATS },
    delivered: 'list',
    skipped: 'list'
  } satisfies ResultFieldTypes<RunReportResult>,
  'notification.create': { created: 'list', skipped: 'list' } satisfies ResultFieldTypes<NotificationCreateResult>,
  // The one nested result: each fetched entity is a curated payload model whose
  // scalar fields are addressed as `steps.<id>.result.<entity>.<field>`. The
  // per-entity `satisfies` ties each group to the generated `Fetched*` type; the
  // outer `satisfies` ties the group names + `retrieved_at` to `FetchDataResult`.
  'fetch.data': {
    charger: {
      charger_id: 'string',
      charger_name: 'string',
      site_id: 'string',
      status: 'string',
      operational_status: 'string',
      connectivity_status: 'string',
      vendor: 'string',
      model: 'string',
      firmware_version: 'string',
      max_power: 'number',
      last_heard_at: 'datetime'
    } satisfies ResultFieldTypes<FetchedCharger>,
    vehicle: {
      vehicle_id: 'string',
      vehicle_name: 'string',
      organization_id: 'string',
      state_of_charge: 'number',
      vehicle_status: 'string',
      latitude: 'number',
      longitude: 'number',
      remaining_range: 'number',
      odometer: 'number',
      home_site_id: 'string'
    } satisfies ResultFieldTypes<FetchedVehicle>,
    site: {
      site_id: 'string',
      site_name: 'string',
      timezone: 'string',
      power_ceiling: 'number',
      num_chargers: 'number',
      num_vehicles: 'number',
      latitude: 'number',
      longitude: 'number',
      address: 'string'
    } satisfies ResultFieldTypes<FetchedSite>,
    retrieved_at: 'datetime'
  } satisfies Record<keyof FetchDataResult, ActionResultFieldSchema>
}

/**
 * The result fields worth offering as tokens for an action, as dotted paths
 * relative to `result` (`applied`, `charger.status`): scalar values only.
 * List/object results are omitted because they can't be interpolated into a
 * message body or bound to a scalar parameter (see {@link ActionResultFieldType}).
 * An action with no scalar result fields (the messaging actions, `wait`) yields
 * none, so its step contributes no chips to the palette.
 */
export function templatableResultFields(action: ActionId): readonly string[] {
  const out: string[] = []
  for (const [key, schema] of Object.entries(ACTION_RESULT_CATALOG[action])) {
    if (isResultLeaf(schema)) {
      if (isScalarResultField(leafType(schema))) out.push(key)
      continue
    }
    for (const [leaf, leafSchema] of Object.entries(schema)) {
      if (isScalarResultField(leafType(leafSchema))) out.push(`${key}.${leaf}`)
    }
  }
  return out
}

/**
 * fetch.data's result entity groups keyed by the authoring parameter that
 * requests them: an entity is fetched only when its id parameter is provided,
 * so a step with an unset id resolves that group to `null` on every run.
 */
const FETCH_DATA_ENTITY_PARAMS = {
  charger: 'chargerId',
  vehicle: 'vehicleId',
  site: 'siteId'
} as const satisfies Partial<Record<keyof FetchDataResult, keyof FetchDataParametersAuthoring>>

/**
 * {@link templatableResultFields}, narrowed by the step's authored parameters:
 * a fetch.data step offers only the entity groups it actually requests — a
 * token or condition on an unrequested entity could never resolve to a value
 * (the group is `null` on every run). Other actions have no parameter-dependent
 * result shape and pass through unchanged. Reference *validation* stays
 * permissive (the backend accepts any declared field), so an already-authored
 * reference survives the id being cleared; it just isn't offered anymore.
 */
export function templatableResultFieldsForStep(step: ActionStepDefinition): readonly string[] {
  const fields = templatableResultFields(step.action)
  if (step.action !== 'fetch.data') return fields
  return fields.filter((path) => {
    const entity = Object.entries(FETCH_DATA_ENTITY_PARAMS).find(([group]) => path === group || path.startsWith(`${group}.`))
    if (!entity) return true
    const id = step.parameters[entity[1]]
    return typeof id === 'string' && id.trim() !== ''
  })
}

/**
 * The declared kind of a result field addressed by dotted path (`applied`,
 * `charger.status`), or `undefined` when the path names nothing the action's
 * result declares. A path that stops at a nested group (`charger`) resolves to
 * `object` — the backend accepts a whole-value reference to it, it just isn't
 * scalar. Own-property checks, not `in`: `in` also matches inherited
 * `Object.prototype` keys (`toString`, `constructor`, `__proto__`, …), which
 * would let a crafted `steps.<id>.result.toString` reference pass as "known"
 * (and fieldless actions like `wait` inherit them all) — the backend rejects
 * those, so must we.
 */
export function resultFieldType(action: ActionId, field: string): ActionResultFieldType | undefined {
  const schema = resultFieldSchema(action, field)
  if (schema === undefined) return undefined
  return isResultLeaf(schema) ? leafType(schema) : 'object'
}

/**
 * The members an `enum` result field addressed by dotted path can take, in the
 * order the schema lists them; `undefined` for any other field, or an enum whose
 * members the schema does not list.
 */
export function resultFieldEnumValues(action: ActionId, field: string): readonly string[] | undefined {
  const schema = resultFieldSchema(action, field)
  return schema !== undefined && isEnumResultField(schema) ? schema.enumValues : undefined
}

/** The schema entry a dotted result path names; see {@link resultFieldType}. */
function resultFieldSchema(action: ActionId, field: string): ActionResultFieldSchema | undefined {
  const [head, ...rest] = field.split('.')
  const fields = ACTION_RESULT_CATALOG[action]
  if (head === undefined || head === '' || !Object.hasOwn(fields, head)) return undefined
  const schema = fields[head]!
  if (rest.length === 0) return schema
  if (isResultLeaf(schema) || rest.length > 1) return undefined
  const leaf = rest[0]!
  return Object.hasOwn(schema, leaf) ? schema[leaf] : undefined
}

/**
 * Whether `field` (a dotted path relative to `result`) is a declared result
 * field of `action` — the set the backend templating validator accepts as
 * `steps.<id>.result.<path>` (any declared field, scalar or not; the kind
 * filter is only for what the palette *offers*).
 */
export function isKnownResultField(action: ActionId, field: string): boolean {
  return resultFieldType(action, field) !== undefined
}

/**
 * Split a full step-result condition/template `field` path
 * (`steps.<id>.result.<path>`) into its step id and the result-relative dotted
 * path, or `undefined` when the field is not a step-result reference (an
 * `event.attributes.*` path, a bare attribute key, …).
 */
export function parseStepResultField(field: string): { stepId: string; path: string } | undefined {
  const match = /^steps\.([^.]+)\.result\.(.+)$/.exec(field)
  if (!match) return undefined
  return { stepId: match[1]!, path: match[2]! }
}
